import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  HomeIcon,
  InformationCircleIcon,
  StarIcon,
  WalletIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import {
  connectWallet,
  checkWalletConnection,
  disconnectWallet,
} from "../store/wallet/wallet-actions";
import { useNavigate } from "react-router-dom";
import logoIcon from "../images/logo.svg";
import accountIcon from "../images/account.svg";
import SvgIcon from "./utility/svg-icon";

/**
 * Main navigation component that handles wallet connection, theme switching, and mobile menu
 * @param {Object} props - Component props
 * @param {string} props.theme - Current theme setting
 * @param {Function} props.changeTheme - Function to handle theme changes
 * @param {boolean} props.isOpen - Current state of the mobile sidebar
 * @param {Function} props.toggleSidebar - Function to toggle the mobile sidebar
 * @returns {JSX.Element} Rendered navigation bar
 */
export default function Navigation({ theme, changeTheme }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const config = useSelector((state) => state.config);
  const [themes, setThemes] = useState([]);

  useEffect(() => {
    if (config?.clientConfig?.data?.collections) {
      setThemes(config.clientConfig.data.theming.themes);
    }
  }, [config]);
  /**
   * Effect hook to check wallet connection status on component mount
   */
  useEffect(() => {
    dispatch(checkWalletConnection());
  }, [dispatch]);

  /**
   * Internal component for wallet connection button
   * Displays either the connected wallet address or connect button
   * @returns {JSX.Element} Rendered wallet connection button
   */
  const ConnectWalletButton = () => {
    const { isConnected, isConnecting, address, error, balanceError } = useSelector((state) => state.wallet);

    return (
      <div>
        <button disabled={isConnecting} onClick={() => dispatch(connectWallet())}>
          {isConnected
            ? `${address.slice(0, 4)}.......${address.slice(-4)}`
            : isConnecting ? "Connessione…" : "Connect Wallet"}
        </button>
        {error && <p role="alert">{error}</p>}
        {balanceError && <p role="status">Saldo non disponibile. La connessione al wallet è attiva.</p>}
      </div>
    );
  };

  /**
   * Handles wallet disconnection
   * Dispatches disconnect action and removes connection from local storage
   */
  const handleDisconnect = () => {
    dispatch(disconnectWallet());
  };

  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const dropdownRef = useRef(null); // Create a ref for the dropdown container

  const toggleDropdown = () => {
    setDropdownIsOpen(!dropdownIsOpen);
  };

  // Function to handle clicks outside the dropdown
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownIsOpen(false);
    }
  };

  useEffect(() => {
    // Add event listener when the dropdown is open
    if (dropdownIsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      // Remove event listener when the dropdown is closed
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownIsOpen]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 1);

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /**
   * Component render section
   * Renders the navigation bar with mobile menu toggle, theme selector, and wallet controls
   */
  return (
    <nav
      className={`navbar-outer-container container-cust-fluid ${isScrolled ? "scrolled" : ""}`}
    >
      <div className="navbar-boxed-container container-cust">
        <div className="left-section">
          <a className="brand flex items-center" href="/">
            <SvgIcon
              src={logoIcon}
              color="var(--theme-color-accent)"
              size={20}
            />
            {config?.clientConfig?.data?.logoImgUrl && (
              <img
                src={config.clientConfig.data.logoImgUrl}
                alt="Logo"
                className="h-10 max-h-full object-contain"
              />
            )}
            {config?.clientConfig?.data?.name && (
              <span className="client-name">
                {config.clientConfig.data.name}
              </span>
            )}
          </a>
        </div>

        <div className="right-section" ref={dropdownRef}>
          <button
            className="account-button"
            onClick={toggleDropdown}
            aria-expanded={dropdownIsOpen}
          >
            <SvgIcon
              src={accountIcon}
              color="var(--theme-color-accent)"
              size={30}
            />
          </button>

          {dropdownIsOpen && (
            <ul className="dropdown-button">
              <li>
                <select onChange={changeTheme} value={theme} className="">
                  {themes &&
                    themes.map((theme) => (
                      <option key={theme.name} value={theme.name}>
                        {theme.name}
                      </option>
                    ))}
                </select>
              </li>

              <li>
                <button
                  className="appearance-none"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/admin");
                  }}
                >
                  Admin area
                </button>
              </li>

              <li>{<ConnectWalletButton />}</li>

              <li>
                <button onClick={handleDisconnect}>Logout</button>
              </li>

              <li>
                <Link to="/" className="flex items-center">
                  <HomeIcon className="w-5 h-5 mr-2" /> Home
                </Link>
              </li>

              <li>
                <Link to="/collections" className="flex items-center">
                  <StarIcon className="w-5 h-5 mr-2" /> Collections
                </Link>
              </li>

              <li>
                <Link to="/claim" className="flex items-center">
                  <WalletIcon className="w-5 h-5 mr-2" /> Test claim
                </Link>
              </li>

              <li>
                <Link to="/nft-gallery" className="flex items-center">
                  <GlobeAltIcon className="w-5 h-5 mr-2" /> My assets
                </Link>
              </li>

              <li>
                <Link to="/about" className="flex items-center">
                  <InformationCircleIcon className="w-5 h-5 mr-2" /> About
                </Link>
              </li>
            </ul>
          )}
        </div>
      </div>
    </nav>
  );
}
